import $ from "jquery";
import Swiper from "swiper";
import { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade } from "swiper/modules";
Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade]);
window.Swiper = Swiper;

import "swiper/css/bundle";

// Define slider block configurations - single source of truth
const SLIDER_CONFIG = {
    ".ms_content-carousel": { main: ".swiper", thumb: null, selector: "swiper" },
    ".ms_carousel": { main: ".carousel", thumb: ".thumbnail-carousel", selector: "carousel" },
    ".ms_cards": [
        { main: ".card-slider", thumb: null, selector: "card-slider" },
        { main: ".card-mobile-slider", thumb: null, selector: "card-mobile-slider" },
    ],
    ".ms_products": [
        { main: ".product-slider", thumb: null, selector: "product-slider" },
        { main: ".product-mobile-slider", thumb: null, selector: "product-mobile-slider" },
    ],
    ".ms_testimonial-quote": { main: ".item-slider", thumb: null, selector: "item-slider" },
    ".ms_usps": { main: ".usps-slider", thumb: null, selector: "usps-slider" },
};

// Generate selectors from config
const SLIDER_SELECTORS = {
    containers: Object.keys(SLIDER_CONFIG).join(", "),
    swipers: Object.values(SLIDER_CONFIG)
        .flatMap((cfg) => (Array.isArray(cfg) ? cfg : [cfg]))
        .flatMap((cfg) => [cfg.main, cfg.thumb].filter(Boolean))
        .join(", "),
};

const initializeSliders = function (sliders) {
    // Helper to parse options from data attribute
    const parseOptions = (el) => (el ? JSON.parse(el.dataset.sliderOptions || "{}") : {});

    // Helper to calculate and apply container alignment
    const applyContainerAlignment = (options, mainEl, sliderId) => {
        const shouldAlign = options.alignWithContainer === true;

        if (!shouldAlign) {
            return;
        }

        const calculateOffset = () => {
            const containerEl = document.querySelector(".container");
            const sliderEl = document.querySelector(`#${sliderId} ${mainEl.classList.contains('swiper') ? '.swiper' : '.' + Array.from(mainEl.classList).find(c => c.includes('slider') || c.includes('carousel') || c === mainEl.className)}`);

            if (containerEl && sliderEl) {
                const containerRect = containerEl.getBoundingClientRect();
                const sliderRect = sliderEl.getBoundingClientRect();

                // Get container's left padding
                const containerPaddingLeft = parseFloat(window.getComputedStyle(containerEl).paddingLeft) || 0;

                const containerOffset = containerRect.left + window.scrollX + containerPaddingLeft;
                const sliderOffset = sliderRect.left + window.scrollX;
                const offsetValue = Math.max(0, containerOffset - sliderOffset);

                if (offsetValue > 0) {
                    options.slidesOffsetBefore = offsetValue;
                    options.slidesOffsetAfter = offsetValue;
                }
            }
        };

        // Calculate initial offset
        calculateOffset();

        // Recalculate on resize. Returned so a re-init can drop it -- this used
        // to be added on every init and never removed.
        window.addEventListener("resize", calculateOffset);

        return () => window.removeEventListener("resize", calculateOffset);
    };

    // Helper to assign unique navigation button classes and update options
    const assignNavButtons = (sliderId, options, type = "main") => {
        ["prev", "next"].forEach((btn) => {
            const selector =
                type === "main"
                    ? `#${sliderId} .swiper-button-${btn}.main-button, #${sliderId} .swiper-button-${btn}`
                    : `#${sliderId} .swiper-button-${btn}.thumbnail-button`;
            const button = document.querySelector(selector);
            if (button) {
                const btnClass = `${sliderId}-${type}-${btn}`;
                button.classList.add(btnClass);
                options.navigation = options.navigation || {};
                options.navigation[`${btn}El`] = `.${btnClass}`;
            }
        });
    };

    // Helper to handle reduced motion settings
    const applyReducedMotion = (options, reduced) => {
        if (reduced) {
            options.autoplay = false;
            options.speed = 0;
        }
    };

    // Helper to (re)initialize Swiper instances
    const reinitSlider = (selector, options, onSlideChange) => {
        const instance = new Swiper(selector, options);
        if (onSlideChange) instance.on("slideChange", onSlideChange);
        return instance;
    };

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const hasThumb = sliders.thumb != null;

    // The thumb path has always hardcoded `.carousel` for the main slider while
    // the plain path honours `sliders.selector`. Kept as it is: changing it
    // would move which element a thumbed block initializes.
    const mainSelector = hasThumb
        ? `#${sliders.id} .carousel`
        : sliders?.selector
          ? `#${sliders.id} .${sliders.selector}`
          : `#${sliders.id} .carousel`;
    const thumbSelector = `#${sliders.id} .thumbnail-carousel`;

    const mainOptions = parseOptions(sliders.main);
    assignNavButtons(sliders.id, mainOptions, "main");
    const stopAlignment = applyContainerAlignment(mainOptions, sliders.main, sliders.id);

    // Snapshots taken BEFORE reduced motion is applied, so every build starts
    // from the same pristine options rather than from a mutated set.
    const originalMainOptions = JSON.parse(JSON.stringify(mainOptions));

    let originalThumbOptions = null;
    if (hasThumb) {
        const thumbOptions = parseOptions(sliders.thumb);
        assignNavButtons(sliders.id, thumbOptions, "thumb");
        originalThumbOptions = JSON.parse(JSON.stringify(thumbOptions));
    }

    let mainSlider = null;
    let thumbSlider = null;

    function slideChange() {
        const currentSlide = mainSlider.slides[mainSlider.activeIndex];
        if (!currentSlide) return;
        if (currentSlide.classList.contains("youtube")) {
            const iframe = currentSlide.querySelector("iframe");
            // Uncomment to play video:
            // if (iframe) iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else {
            mainSlider.slides.forEach((slide) => {
                const iframe = slide.querySelector("iframe");
                if (iframe) {
                    iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
                }
            });
        }
    }

    const build = (reduceMotion) => {
        const main = JSON.parse(JSON.stringify(originalMainOptions));
        applyReducedMotion(main, reduceMotion);

        if (!hasThumb) {
            mainSlider = reinitSlider(mainSelector, main);
            return;
        }

        const thumb = JSON.parse(JSON.stringify(originalThumbOptions));
        applyReducedMotion(thumb, reduceMotion);

        thumbSlider = reinitSlider(thumbSelector, thumb);
        main.thumbs = { swiper: thumbSlider };
        mainSlider = reinitSlider(mainSelector, main, slideChange);
    };

    const destroySliders = () => {
        mainSlider?.destroy();
        thumbSlider?.destroy();
        mainSlider = null;
        thumbSlider = null;
    };

    const onMotionChange = () => {
        destroySliders();
        build(mediaQuery.matches);
    };

    // Re-entry is real: DOMContentLoaded, the editor's MutationObserver, ACF's
    // render_block_preview and the exported window.initializeSliders all reach
    // here. Without this, each pass left its Swipers and its media-query
    // listener behind, so one motion change fired N handlers closed over
    // destroyed instances.
    // window.initializeSliders is public, so a theme may call this without a
    // main element; parseOptions already tolerated that.
    if (sliders.main) {
        sliders.main.soilSliderTeardown?.();
        sliders.main.soilSliderTeardown = () => {
            destroySliders();
            mediaQuery.removeEventListener("change", onMotionChange);
            stopAlignment?.();
        };
    }

    mediaQuery.addEventListener("change", onMotionChange);
    build(!mediaQuery || mediaQuery.matches);

    if (!hasThumb && typeof cardSliders !== "undefined") {
        cardSliders = cardSliders.map(({ container, id }) => {
            if (id === sliders.id) {
                return { id, container, instance: mainSlider };
            }
            return { id, container };
        });
    }
};
window.initializeSliders = initializeSliders;

// Apply above function to all sliders.
function msInitializeDynamicSliders() {
    let listOfSliders = [];

    // Loop through each slider block type from config
    Object.entries(SLIDER_CONFIG).forEach(([containerSelector, config]) => {
        document.querySelectorAll(containerSelector).forEach((container) => {
            // Handle both single config and array of configs
            const configs = Array.isArray(config) ? config : [config];

            configs.forEach((cfg) => {
                const mainElement = container.querySelector(cfg.main);
                if (mainElement) {
                    listOfSliders.push({
                        id: container.id,
                        main: mainElement,
                        thumb: cfg.thumb ? container.querySelector(cfg.thumb) : null,
                        selector: cfg.selector,
                    });
                }
            });
        });
    });

    if (listOfSliders.length !== 0) {
        listOfSliders.forEach((sliders) => {
            initializeSliders(sliders);
        });
    }
}

document.addEventListener(
    `DOMContentLoaded`,
    function () {
        msInitializeDynamicSliders();
    },
    { passive: true },
);

// For WordPress block editor iframe support
if (typeof wp !== "undefined" && wp.hooks && typeof acf === "undefined") {
    // Use MutationObserver to detect when new slider elements are added to the iframe
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setupMutationObserver);
    } else {
        setupMutationObserver();
    }

    function setupMutationObserver() {
        let mutationTimeout;
        let isReinitializing = false;

        const observer = new MutationObserver(() => {
            // Skip if we're already reinitializing
            if (isReinitializing) {
                return;
            }

            // Debounce to prevent infinite loop from DOM modifications during initialization
            clearTimeout(mutationTimeout);
            mutationTimeout = setTimeout(() => {
                // Check if any swiper element doesn't have the swiper-initialized class
                const swiperSelector = SLIDER_SELECTORS.swipers
                    .split(", ")
                    .map((sel) => `${sel}:not(.swiper-initialized)`)
                    .join(", ");
                const uninitializedSwiper = document.querySelector(swiperSelector);

                if (uninitializedSwiper) {
                    isReinitializing = true;
                    msInitializeDynamicSliders();
                    // Reset flag after initialization completes
                    setTimeout(() => {
                        isReinitializing = false;
                    }, 500);
                }
            }, 300); // Wait 300ms after mutations stop before reinitializing
        });

        observer.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false,
        });
    }
}

if (typeof acf !== "undefined") {
    // ACF hook for non-iframed editors
    acf.addAction(`render_block_preview`, function ($el, attributes) {
        msInitializeDynamicSliders();
    });
}

$(".swiper-button-next, .swiper-button-prev").on("mousedown", function (e) {
    e.preventDefault();
});
